import type { ArcgisFeatureLike, KpiDefinition, RouteMetric, TerminalOccupancy, TrendPoint } from "./types";

export const TREND_WINDOW_MS = 24 * 60 * 60 * 1000;

const TERMINALS: Array<{ id: string; label: string; vesselsField: string; pctField: string }> = [
  { id: "tuas", label: "Tuas", vesselsField: "occ_tuas_vessels", pctField: "occ_tuas_pct" },
  { id: "ppanjang", label: "Pasir Panjang", vesselsField: "occ_ppanjang_vessels", pctField: "occ_ppanjang_pct" },
  { id: "jit", label: "JIT", vesselsField: "occ_jit_vessels", pctField: "occ_jit_pct" },
  { id: "keppel", label: "Keppel", vesselsField: "occ_keppel_vessels", pctField: "occ_keppel_pct" },
  { id: "brani", label: "Brani", vesselsField: "occ_brani_vessels", pctField: "occ_brani_pct" },
  { id: "jurong", label: "Jurong", vesselsField: "occ_jurong_vessels", pctField: "occ_jurong_pct" }
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function attrs(feature: ArcgisFeatureLike): Record<string, unknown> {
  return isRecord(feature.attributes) ? feature.attributes : {};
}

function stringValue(record: Record<string, unknown>, key: string, fallback = ""): string {
  const value = record[key];
  if (typeof value === "string") {
    return value;
  }
  if (typeof value === "number") {
    return String(value);
  }
  return fallback;
}

function numberValue(record: Record<string, unknown>, key: string): number | null {
  const value = record[key];
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function timestampValue(record: Record<string, unknown>, key: string): number | null {
  const value = numberValue(record, key);
  return value === null || value <= 0 ? null : value;
}

export function stripLayerIndex(url: string): string {
  return url.replace(/\/\d+\/?$/, "");
}

export function layerUrl(url: string, layerIndex: number): string {
  return `${stripLayerIndex(url).replace(/\/$/, "")}/${layerIndex}`;
}

export function formatNumber(value: number | null, fractionDigits = 0): string {
  if (value === null || !Number.isFinite(value)) {
    return "-";
  }
  return new Intl.NumberFormat("en-SG", {
    maximumFractionDigits: fractionDigits,
    minimumFractionDigits: fractionDigits
  }).format(value);
}

export function formatPercent(value: number | null): string {
  return value === null ? "-" : `${formatNumber(value, value % 1 === 0 ? 0 : 1)}%`;
}

export function formatDateTime(epochMs: number | null): string {
  if (epochMs === null) {
    return "No timestamp";
  }
  return new Intl.DateTimeFormat("en-SG", {
    dateStyle: "medium",
    timeStyle: "short",
    hour12: false,
    timeZone: "Asia/Singapore"
  }).format(new Date(epochMs));
}

export function formatTrendDateLabel(epochMs: number): string {
  return new Intl.DateTimeFormat("en-SG", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Singapore"
  }).format(new Date(epochMs)).replace(", ", "\n");
}

export function freshnessLabels(
  metricsTimestamp: number | null,
  vesselTimestamp: number | null
): { metrics: string; vessels: string } {
  return {
    metrics:
      metricsTimestamp === null
        ? "Metrics unavailable"
        : `Metrics updated ${formatDateTime(metricsTimestamp)} SGT`,
    vessels:
      vesselTimestamp === null
        ? "Vessel positions unavailable"
        : `Vessel positions as of ${formatDateTime(vesselTimestamp)} SGT`
  };
}

export function latestTimestamp(features: ArcgisFeatureLike[], field: string): number | null {
  if (features.length === 0) {
    return null;
  }
  return timestampValue(attrs(features[0] ?? {}), field);
}

export function normalizeRoutes(features: ArcgisFeatureLike[]): RouteMetric[] {
  return features
    .map((feature) => {
      const record = attrs(feature);
      const terminalOccupancy: TerminalOccupancy[] = TERMINALS.map((terminal) => ({
        id: terminal.id,
        label: terminal.label,
        vessels: numberValue(record, terminal.vesselsField),
        percent: numberValue(record, terminal.pctField)
      }));

      return {
        routeSort: numberValue(record, "m12_route_sort") ?? 0,
        routeId: stringValue(record, "m12_route_id", "UNKNOWN"),
        routeName: stringValue(record, "m12_route_name", "Unnamed route"),
        origin: stringValue(record, "m12_origin", "-"),
        transshipmentPort: stringValue(record, "m12_transshipment_port", "-"),
        destination: stringValue(record, "m12_destination", "-"),
        cargoArchetype: stringValue(record, "m12_cargo_archetype", "-"),
        singaporeWeight: numberValue(record, "m12_singapore_weight"),
        primaryUser: stringValue(record, "m12_primary_user", "-"),
        decisionContext: stringValue(record, "m12_decision_context", "-"),
        delayExposureScore: numberValue(record, "m12_delay_exposure_score"),
        delayExposureCategory: stringValue(record, "m12_delay_exposure_category", "No data"),
        topDriver: stringValue(record, "m12_top_driver", "-"),
        recommendedAction: stringValue(record, "m12_recommended_action", "-"),
        caveat: stringValue(record, "m12_caveat", ""),
        calcTimestamp: timestampValue(record, "calc_timestamp"),
        anchoragePressure: numberValue(record, "anchorage_queue_depth"),
        arrivalSurge6h: numberValue(record, "arrival_surge_6h"),
        arrivalSurge12h: numberValue(record, "arrival_surge_12h"),
        dwellP50: numberValue(record, "dwell_time_p50"),
        arrivalRateHourly: numberValue(record, "arrival_rate_hourly"),
        departureRateHourly: numberValue(record, "departure_rate_hourly"),
        netAccumulation: numberValue(record, "net_accumulation"),
        trafficIntensityPct: numberValue(record, "traffic_intensity_pct"),
        congestionScore: numberValue(record, "congestion_score"),
        congestionCategory: stringValue(record, "congestion_category", "No data"),
        scoreStatus: stringValue(record, "score_status", "UNKNOWN"),
        scoreConfidencePct: numberValue(record, "score_confidence_pct"),
        queueContribution: numberValue(record, "score_queue_contrib"),
        surgeContribution: numberValue(record, "score_surge_contrib"),
        dwellContribution: numberValue(record, "score_dwell_contrib"),
        accumulationContribution: numberValue(record, "score_accum_contrib"),
        terminalOccupancy
      } satisfies RouteMetric;
    })
    .sort((left, right) => left.routeSort - right.routeSort || left.routeName.localeCompare(right.routeName));
}

export function normalizeTrends(features: ArcgisFeatureLike[]): TrendPoint[] {
  return features
    .map((feature) => {
      const record = attrs(feature);
      return {
        timestamp: timestampValue(record, "calc_timestamp") ?? 0,
        congestionScore: numberValue(record, "congestion_score"),
        anchoragePressure: numberValue(record, "anchorage_queue_depth"),
        arrivalSurge6h: numberValue(record, "arrival_surge_6h"),
        arrivalRateHourly: numberValue(record, "arrival_rate_hourly"),
        departureRateHourly: numberValue(record, "departure_rate_hourly")
      } satisfies TrendPoint;
    })
    .filter((point) => point.timestamp > 0)
    .sort((left, right) => left.timestamp - right.timestamp);
}

export function trendWindowStart(referenceTime = Date.now()): number {
  return referenceTime - TREND_WINDOW_MS;
}

export function last24HourTrends(
  trends: TrendPoint[],
  referenceTime = Date.now()
): TrendPoint[] {
  const cutoff = trendWindowStart(referenceTime);
  return trends.filter((point) => point.timestamp >= cutoff && point.timestamp <= referenceTime);
}

function percentile(sortedValues: number[], percentileValue: number): number | null {
  if (sortedValues.length === 0) {
    return null;
  }
  const index = (sortedValues.length - 1) * percentileValue;
  const lowerIndex = Math.floor(index);
  const upperIndex = Math.ceil(index);
  const lowerValue = sortedValues[lowerIndex];
  const upperValue = sortedValues[upperIndex];
  if (lowerValue === undefined || upperValue === undefined) {
    return null;
  }
  return lowerValue + (upperValue - lowerValue) * (index - lowerIndex);
}

function suppressExtremeOutliers(values: Array<number | null>): Array<number | null> {
  const numericValues = values
    .filter((value): value is number => value !== null && Number.isFinite(value))
    .sort((left, right) => left - right);
  if (numericValues.length < 8) {
    return values;
  }

  const q1 = percentile(numericValues, 0.25);
  const q3 = percentile(numericValues, 0.75);
  if (q1 === null || q3 === null) {
    return values;
  }

  const iqr = Math.max(q3 - q1, 0);
  const upperFence = Math.max(q3 + iqr * 6, q3 * 4, 250);
  return values.map((value) => (value !== null && value > upperFence ? null : value));
}

export function chartDisplayTrends(trends: TrendPoint[]): TrendPoint[] {
  const anchorageValues = suppressExtremeOutliers(trends.map((point) => point.anchoragePressure));
  return trends.map((point, index) => ({
    ...point,
    anchoragePressure: anchorageValues[index] ?? null
  }));
}

export function chooseRoute(routes: RouteMetric[], selectedRouteId: string | null): RouteMetric | null {
  if (routes.length === 0) {
    return null;
  }
  return routes.find((route) => route.routeId === selectedRouteId) ?? routes[0] ?? null;
}

export function scoreBand(route: RouteMetric | null): "stale" | "low" | "medium" | "high" | "critical" {
  if (route === null || route.congestionScore === null || route.scoreStatus.toUpperCase() === "STALE") {
    return "stale";
  }
  const category = route.congestionCategory.toUpperCase();
  if (category.includes("CRITICAL")) {
    return "critical";
  }
  if (category.includes("HIGH")) {
    return "high";
  }
  if (category.includes("MEDIUM")) {
    return "medium";
  }
  return "low";
}

export function buildKpis(route: RouteMetric | null): KpiDefinition[] {
  if (route === null) {
    return [
      { id: "anchorage", label: "Anchorage", value: "-", caption: "vessels", icon: "anchor" },
      { id: "surge", label: "Arrival Surge", value: "-", caption: "next 6h", icon: "waves" },
      { id: "dwell", label: "Dwell P50", value: "-", caption: "hours", icon: "clock" },
      { id: "flow", label: "Net Flow", value: "-", caption: "vessels/hr", icon: "activity" }
    ];
  }
  return [
    {
      id: "anchorage",
      label: "Anchorage",
      value: formatNumber(route.anchoragePressure),
      caption: "slow/stationary",
      icon: "anchor"
    },
    {
      id: "surge",
      label: "Arrival Surge",
      value: formatNumber(route.arrivalSurge6h),
      caption: "next 6h",
      icon: "waves"
    },
    {
      id: "dwell",
      label: "Dwell P50",
      value: formatNumber(route.dwellP50, 1),
      caption: "hours",
      icon: "clock"
    },
    {
      id: "flow",
      label: "Net Flow",
      value: formatNumber(route.netAccumulation, 1),
      caption: "vessels/hr",
      icon: "activity"
    }
  ];
}
